import { DeleteOutlined } from '@ant-design/icons'
import { clsx } from 'clsx'
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import styles from './milestone-check.module.scss'
import { PureButton } from '../../../../../common/ui/pure-button/pure-button'
import { PureTable } from '../../../../../common/ui/pure-table/pure-table'
import { PureTextArea } from '../../../../../common/ui/pure-text-area/pure-text-area'
import { ObjectItem } from '../../../../../entities/objects/types/object.types'
import { MilestoneChecklistStatus } from '../../../../../entities/workflow/constants/workflow.constants'
import { useGetChecklist, useUpdateChecklist } from '../../../../../entities/workflow/hooks/workflow.hooks'
import { Checklist, MilestoneItem } from '../../../../../entities/workflow/types/workflow.types'
import { FormItemType } from '../../../../shared/form-builder/constants/form-builder.constants'
import { FormBuilderConfig } from '../../../../shared/form-builder/types/form-builder.types'
import { getMilestoneCheckTableColumns } from '../../helpers/get-milestone-check-table-columns'

type MilestoneCheckProps = {
  milestone: MilestoneItem
  object: ObjectItem
}

export const MilestoneCheck = forwardRef(({ milestone, object }: MilestoneCheckProps, ref) => {
  const { t } = useTranslation()
  const checklist = useGetChecklist(object.id, milestone.id)
  const { mutateAsync: updateChecklist } = useUpdateChecklist()
  const [checklistComments, setChecklistComments] = useState<Record<string, string[]>>({})
  const [selectedItem, setSelectedItem] = useState<FormBuilderConfig | null>(null)
  const [commentError, setCommentError] = useState<string | null>()
  const [columns, setColumns] = useState<any[]>([])
  const [comment, setComment] = useState<string>('')

  const submitChecklist = async (status: MilestoneChecklistStatus) => {
    if (!checklist.data?.factStart) {
      return true
    }
    if (checklist?.data) {
      const checklistInfo = checklist.data
      const data: Checklist = {
        ...checklistInfo,
        status,
        comment: { ...checklistComments },
      }
      try {
        await updateChecklist(data)

        return true
      } catch (e) {
        toast.error(t('workspace.checkSaveError'))

        return false
      }
    }
  }

  useImperativeHandle(ref, () => ({
    submit: submitChecklist,
  }))

  useEffect(() => {
    const columns: any = getMilestoneCheckTableColumns(t, checklist.data)
    setColumns(columns)
    setChecklistComments(checklist.data?.comment ?? {})
  }, [checklist.data])

  if (!milestone) {
    return <div>{t('workspace.noMilestone')}</div>
  }

  if (milestone && checklist.isLoading) {
    return <div>{t('common.loading')}</div>
  }

  if (!checklist.data?.factStart) {
    return <div className="warning-text">{t('workspace.checkImpossible')}</div>
  }

  const handleItemSelect = (configItem: FormBuilderConfig) => {
    if (configItem.type === FormItemType.Header) {
      return
    }
    if (selectedItem && selectedItem.id === configItem.id) {
      setSelectedItem(null)
    } else {
      setSelectedItem(configItem)
    }
  }

  const handleCommentSave = (value: string) => {
    if (commentError) {
      setCommentError(null)
    }
    if (!value) {
      setCommentError('Комментарий обязателен')

      return
    }
    if (selectedItem) {
      const selectedItemComments = checklistComments[selectedItem.id] ?? []
      checklistComments[selectedItem.id] = [...selectedItemComments, value]
      setChecklistComments({ ...checklistComments })
    }
    setComment('')
  }

  const handleCommentKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key.toLowerCase() === 'enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleCommentSave(e.currentTarget.value)
    }
  }

  const handleCommentChange = (value: string) => {
    setComment(value)
  }

  const deleteComment = (index: number) => {
    if (selectedItem) {
      checklistComments[selectedItem.id]?.splice(index, 1)
      setChecklistComments({ ...checklistComments })
    }
  }

  return (
    <div className={clsx('d-flex')}>
      <div className={clsx('w-50', 'p-2')}>
        <PureTable
          columns={columns}
          dataSource={milestone?.checkList}
          onRow={(configItem) => ({
            onClick: () => handleItemSelect(configItem),
            className: configItem.type !== FormItemType.Header ? styles.tableHoveredRow : '',
          })}
          showHeader={false}
        />
      </div>
      <div className={clsx('w-50', 'p-2')}>
        {selectedItem ? (
          <div className={clsx('d-flex flex-column')}>
            <h6>Комментарий</h6>
            <div>
              <PureTextArea
                error={commentError ? String(commentError) : ''}
                onChange={handleCommentChange as any}
                onKeyDown={handleCommentKey}
                value={comment}
              />
              <PureButton
                className={clsx('mt-2')}
                onClick={() => handleCommentSave(comment)}
                size="small"
                type="primary"
              >
                {t('common.add')}
              </PureButton>
            </div>
            {checklistComments[selectedItem.id]
              ? checklistComments[selectedItem.id]?.map((comment: string, i: number) => (
                  <div
                    key={i}
                    className={clsx(styles.commentItem, 'd-flex', 'justify-content-between', 'align-items-center')}
                  >
                    <div className={clsx('col-11')}>{comment}</div>
                    <PureButton
                      color="danger"
                      onClick={() => deleteComment(i)}
                      size="small"
                      variant="text"
                    >
                      <DeleteOutlined />
                    </PureButton>
                  </div>
                ))
              : null}
          </div>
        ) : null}
      </div>
    </div>
  )
})
